import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'
import type { MemberInvite } from './inviteInput'

/** What the API needs: the normalized invite plus the language to write it in. */
export type MemberInviteRequest = MemberInvite & { locale: string }

/**
 * Invite a member to a team by email or SMS. Posts the normalized invite (see
 * {@link parseInviteInput}) and invalidates the group members query so any
 * resulting member shows up. Authorization (owner/admin) is enforced by the
 * backend, which also composes and sends the message.
 *
 * `locale` is the browser's active language. The recipient is usually not a user
 * yet, so there is no stored preference for the server to read — the person doing
 * the inviting is the best available guess at a language they share.
 */
export function useInviteMember(groupId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invite: MemberInviteRequest) =>
      call<{ status: string }>(
        `/clubhouse/groups/${encodeURIComponent(groupId ?? '')}/members/invite`,
        { method: 'POST', body: JSON.stringify(invite) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', groupId, 'members'] })
    },
    onError: (e) => {
      console.error('Failed to invite member', e)
    },
  })
}
